/**
 * CetusEd NextGen — the ONE response guard.
 *
 * Extracted from ./client.ts so both the hono `hc` client AND the auth
 * transport (./auth-endpoints.ts, which does not go through hc) resolve every
 * response through the SAME non-2xx-throws boundary. ./client.ts re-exports
 * `unwrap` so existing hooks keep importing it from there.
 *
 * ⛔3 — THIS IS ONE DEFECT AT ONE CHOKE POINT, NOT 42 BUGS.
 *
 * `fetch` only rejects on network failure, and hono's `hc` returns a
 * ClientResponse without throwing on non-2xx, so `.ok` is the caller's job. A
 * 4xx/5xx that is not checked RESOLVES: the error body parses, the promise
 * fulfils, React Query fires `onSuccess`, and the UI reports success. For auth
 * that means "logged in" over a 401, or "logged out" over a refusal — a
 * fabricated auth state (⛔4). So a non-2xx throws here, once, for everybody.
 *
 * ⚠ Do not "simplify" a call site back to `.then((res) => res.json())`. That is
 * the defect, and it looks exactly like tidy code.
 */
import { AppError } from '@cetused/shared'

/**
 * The part of a fetch/hono `ClientResponse` this guard actually needs.
 * Structural on purpose so the call site's own `json()` return type flows
 * through as `T` with no assertion (see the long note this replaced in
 * client.ts).
 */
export interface GuardableResponse<T> {
  ok: boolean
  status: number
  statusText: string
  json: () => Promise<T>
}

/** A non-2xx throws (as an AppError); a 2xx returns the parsed body. */
export async function unwrap<T>(res: GuardableResponse<T>): Promise<T> {
  if (!res.ok) {
    throw await toAppError(res)
  }
  return res.json()
}

/**
 * Turn a non-2xx response into an AppError, preferring the server's own
 * message. When the body is not the `{ success, error, code }` envelope — or
 * not JSON at all (a proxy error page, a 404 from a route that does not exist) —
 * we say so plainly rather than inventing a reason. Never let a parse failure
 * here become a success upstream.
 */
async function toAppError(res: GuardableResponse<unknown>): Promise<AppError> {
  let serverMessage = ''
  try {
    const body: unknown = await res.json()
    if (body !== null && typeof body === 'object' && 'error' in body) {
      const { error } = body as { error?: unknown }
      if (typeof error === 'string' && error.length > 0) serverMessage = error
    }
  } catch {
    // Body was absent or not JSON. Fall through to the status-only message —
    // an unreadable error body is still an error, never a success.
    serverMessage = ''
  }

  const detail = serverMessage || res.statusText || 'no error detail in response'
  const message = `Request failed (HTTP ${res.status}): ${detail}`

  if (res.status === 401) return AppError.unauthorized(message)
  if (res.status === 403) return AppError.forbidden(message)
  if (res.status === 404) return AppError.notFound(message)
  if (res.status === 409) return AppError.conflict(message)
  if (res.status === 429) return AppError.rateLimited(message)
  if (res.status === 501) return AppError.notImplemented(message)
  if (res.status >= 400 && res.status < 500) return AppError.badRequest(message)
  return AppError.internal(message)
}
